/// <amd-module name="Wit/BoardsHub/KanbanBoard/ISwimlaneRulesAutoMoveService" />

import { EmptyGuidString } from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { BoardColumn, BoardRow } from "Wit/BoardsHub/Common/Client/Contracts/Settings";
import { IBoardsWorkItem, IObservableBoardsWorkItem } from "Wit/BoardsHub/Common/Interfaces";
import { BoardData } from "Wit/BoardsHub/Common/KanbanBoardContracts";
import { divyCardRules } from "Wit/BoardsHub/Common/StylesUtils";
import { areSwimlaneRulesNonEmpty, getSwimlaneRulesFromRules, ISwimlaneRules } from "Wit/BoardsHub/Common/SwimlaneUtils";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { IWorkItemSavingService } from "Wit/WorkItemModel/Services/WorkItemSavingService";
import { isInProgress } from "./Utils/BoardUtils";

const BoardsDataProvider = "ms.vss-work-web.kanban-board-content-data-provider";

export interface ISwimlaneRulesAutoMoveService extends IVssService {
    calculateAppropriateSwimlane(workItem: IBoardsWorkItem, targetRowId?: string): Promise<string>;
    initialAutoMove(boardItems: IObservableBoardsWorkItem[], boardData: AutoMoveServiceBoardData): Promise<void>;
    tryAutoMove(boardItem: IBoardsWorkItem): Promise<boolean>;
}

export interface SwimlaneAutoMoveResult {
    workItemId: number;
    rowId: string;
}

interface RowWithRules {
    boardRow: BoardRow;
    swimlaneRules: ISwimlaneRules | undefined;
}

export type AutoMoveServiceBoardData = {
    boardModel: {
        boardSettings: Pick<BoardData["boardModel"]["boardSettings"], "columns" | "rows" | "extensionId" | "boardFields">;
        boardCardSettings: Pick<BoardData["boardModel"]["boardCardSettings"], "styles">;
        board: Pick<BoardData["boardModel"]["board"], "id">;
    };
};

class SwimlaneRulesAutoMoveService extends VssService implements ISwimlaneRulesAutoMoveService {
    private rowsWithRules: RowWithRules[] = [];
    private boardData: AutoMoveServiceBoardData | undefined;

    public override _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this.loadBoardData(pageContext);
    }

    public override _serviceRestart(pageContext: IVssPageContext): void {
        super._serviceRestart(pageContext);
        this.loadBoardData(pageContext);
    }

    public async initialAutoMove(boardItems: IObservableBoardsWorkItem[], boardData: AutoMoveServiceBoardData): Promise<void> {
        this.updateBoardData(boardData);
        const { extensionId, columns, rows, rowFieldName, columnFieldName } = this.destructureBoardData()!;
        const perfService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const perfScenarioName = "WorkItem.Agile.SwimlaneRulesAutoMove";
        perfService.startScenario(perfScenarioName, false);
        let perfEventProperties = {};

        const boardItemsSaving: IObservableBoardsWorkItem[] = [];
        const columnsByName = new Map<string, BoardColumn>(columns.map(c => [c.name, c]) ?? []);
        const rowsByName = new Map<string, BoardRow>(rows.map(r => [r.name, r]) ?? []);
        const rowsById = new Map<string, BoardRow>(rows.map(r => [r.id, r]) ?? []);
        const targetRowByWorkItemId = new Map<number, BoardRow>();
        const defaultSwimlane = rows.find(r => r.isDefault);

        for (const boardItem of boardItems) {
            const columnName = boardItem.value.getFieldValue<string>(columnFieldName);
            const column = columnName && columnsByName.get(columnName);
            if (!column || !isInProgress(column)) {
                continue;
            }

            const rowName = boardItem.value.getFieldValue<string>(rowFieldName);
            const row = rowName ? rowsByName.get(rowName) : defaultSwimlane;
            if (!row) {
                continue;
            }
            const autoMoveRowId = await this.calculateAppropriateSwimlane(boardItem.value, row.id);
            const autoMoveRow = autoMoveRowId && rowsById.get(autoMoveRowId);
            if (autoMoveRowId && autoMoveRowId !== row.id && autoMoveRow) {
                boardItemsSaving.push(boardItem);
                targetRowByWorkItemId.set(boardItem.value.id, autoMoveRow);
            }
        }
        perfService.addSplitTiming(`${perfScenarioName}.CalculateAppropriateSwimlane`, perfScenarioName);

        if (boardItemsSaving.length > 0) {
            try {
                const savingService = this.pageContext.getService<IWorkItemSavingService>("IWorkItemSavingService");
                const modelService = this.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");

                const boardItemMap = new Map<number, IObservableBoardsWorkItem>();
                for (const boardItem of boardItemsSaving) {
                    boardItem.saving();
                    boardItemMap.set(boardItem.value.id, boardItem);
                }

                const workItems = await modelService.getWorkItems(boardItemsSaving.map(workItem => workItem.value.id));
                const [extension] = await modelService.getWorkItemTypeExtensions([extensionId]);
                for (const workItem of workItems) {
                    const targetRow = targetRowByWorkItemId.get(workItem.id);
                    if (!targetRow) {
                        continue;
                    }
                    workItem.setExtensions([extension]);
                    workItem.setFieldValueByName(rowFieldName, targetRow.name);
                    const boardItem = boardItemMap.get(workItem.id)?.value;
                    if (boardItem) {
                        boardItem.iWorkItem.value = workItem;
                    }
                }
                perfService.addSplitTiming(`${perfScenarioName}.PrepareForSaving`, perfScenarioName);

                const saveResult = await savingService.saveWorkItemsBatchAsync(workItems, {ignoreWorkItemUpdateTelemetry: true});
                for (const workItem of saveResult.workItems) {
                    const boardItem = boardItemMap.get(workItem.id)?.value;
                    if (boardItem) {
                        boardItem.iWorkItem.value = workItem;
                    }
                }
                perfService.addSplitTiming(`${perfScenarioName}.Saved`, perfScenarioName);
            } catch (err) {
                console.error("swimlaneAutoMoveItems: error", err);
                perfEventProperties = { ...perfEventProperties, error: err?.toString() };
                perfService.addSplitTiming(`${perfScenarioName}.Errored`, perfScenarioName);
            } finally {
                for (const boardItem of boardItemsSaving) {
                    if (boardItem.value.saving) {
                        boardItem.saveComplete();
                    }
                }
            }
        }
        perfService.endScenario("Board", perfScenarioName, false, undefined, {
            ...perfEventProperties,
            autoMovedBoardItems: boardItemsSaving.length,
            swimlanesWithRules: this.rowsWithRules.filter(({ swimlaneRules }) => areSwimlaneRulesNonEmpty(swimlaneRules)).length,
            boardId: this.boardData?.boardModel?.board?.id
        });
    }
    public async tryAutoMove(boardsItem: IBoardsWorkItem): Promise<boolean> {
        const boardData = this.destructureBoardData();
        if (!boardData) {
            return false;
        }
        const { columns, rows, rowFieldName, columnFieldName } = boardData;

        const columnName = boardsItem.getFieldValue<string>(columnFieldName);
        const column = columns.find(c => c.name === columnName);
        if (!isInProgress(column)) {
            return false;
        }

        const rowName = boardsItem.getFieldValue<string>(rowFieldName);
        const row = rows.find(r => r.name === rowName) ?? rows.find(r => r.isDefault);
        if (!row) {
            return false;
        }

        const autoMovedRowId = await this.calculateAppropriateSwimlane(boardsItem, row.id);
        const autoMovedRowName = rows.find(r => r.id === autoMovedRowId)?.name;
        if (autoMovedRowId === row.id) {
            return false;
        } else {
            boardsItem.setFieldValue(rowFieldName, autoMovedRowName);
            return true;
        }
    }

    public async calculateAppropriateSwimlane(workItem: IBoardsWorkItem, targetRowId?: string | undefined): Promise<string> {
        const targetSwimlane = this.rowsWithRules.find(swimlane => swimlane.boardRow.id === targetRowId);
        if (
            targetSwimlane?.swimlaneRules &&
            (await workItem.isCardRuleMatch(this.pageContext, {
                name: targetSwimlane.boardRow.name,
                clauses: targetSwimlane.swimlaneRules.filterClauses
            }))
        ) {
            return targetSwimlane.boardRow.id;
        }

        for (const { boardRow, swimlaneRules } of this.rowsWithRules) {
            if (swimlaneRules && (await workItem.isCardRuleMatch(this.pageContext, { name: boardRow.name, clauses: swimlaneRules.filterClauses }))) {
                return boardRow.id;
            }
        }

        if (targetSwimlane && (!targetSwimlane.swimlaneRules || targetSwimlane.swimlaneRules.filterClauses.length === 0)) {
            return targetSwimlane.boardRow.id;
        }

        for (const { boardRow, swimlaneRules } of this.rowsWithRules) {
            if (swimlaneRules === undefined || swimlaneRules.filterClauses.length === 0) {
                return boardRow.id;
            }
        }

        // always fallback to default row
        return EmptyGuidString;
    }

    private loadBoardData(pageContext: IVssPageContext) {
        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        const boardData = contributionService.getData<BoardData>(BoardsDataProvider);
        boardData && this.updateBoardData(boardData);
    }

    private destructureBoardData():
        | {
              extensionId: string;
              columns: BoardColumn[];
              rows: BoardRow[];
              rowFieldName: string;
              columnFieldName: string;
          }
        | undefined {
        if (!this.boardData) {
            return undefined;
        }
        const {
            boardModel: {
                boardSettings: {
                    extensionId,
                    columns,
                    rows,
                    boardFields: { RowField: rowFieldName, ColumnField: columnFieldName }
                }
            }
        } = this.boardData;
        return {
            extensionId,
            columns,
            rows,
            rowFieldName,
            columnFieldName
        };
    }

    private updateBoardData(boardData: AutoMoveServiceBoardData): void {
        const { swimlaneRules: swimlaneCardRules } = divyCardRules(boardData.boardModel.boardCardSettings.styles);
        const swimlaneRules = getSwimlaneRulesFromRules(swimlaneCardRules);
        const boardRows = boardData.boardModel.boardSettings.rows;
        this.rowsWithRules = [];
        for (const row of boardRows) {
            const rulesForRow = swimlaneRules.find(rule => rule.rowId === row.id);
            this.rowsWithRules.push({
                boardRow: row,
                swimlaneRules: rulesForRow
            });
        }
        this.rowsWithRules.sort((a, b) => a.boardRow.order - b.boardRow.order);
        this.boardData = boardData;
    }
}

const serviceName = "ISwimlaneRulesAutoMoveService";
Services.add(serviceName, { serviceFactory: SwimlaneRulesAutoMoveService });
